import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './checkout.css';
import { I18nProvider, useI18n } from '@react/i18n';
import { trackFunnel } from '@react/funnel';
import { FormProvider, useForm } from 'react-hook-form';
import { ApiError, createCheckoutApi } from './api/checkoutApi';
import { useVatMachine } from './hooks/useVatMachine';
import { useSmartform } from './hooks/useSmartform';
import { CheckoutDraft, clearCheckoutDraft, loadCheckoutDraft, useCheckoutDraftPersist } from './hooks/useCheckoutDraft';
import { BasketSummary, CheckoutFormValues, CheckoutProps, CheckoutState, VatValidateResponse, joinPhonePrefix, normalizeIc, stripPhonePrefix } from './types';
import PersonSection from './sections/PersonSection';
import CompanySection from './sections/CompanySection';
import BillingAddressSection from './sections/BillingAddressSection';
import DeliveryAddressSection from './sections/DeliveryAddressSection';
import AgreementsSection from './sections/AgreementsSection';
import NoteSection from './sections/NoteSection';
import SubmitBar from './sections/SubmitBar';
import { CheckoutSkeleton, flashInputs } from './sections/ui';

/**
 * Poslední krok objednávky řízený Reactem nad /api/v1/checkout.
 *
 * Klíčové vlastnosti:
 * - VAT stavový automat (useVatMachine): DIČ se validuje přes VIES na serveru,
 *   submit je blokován, dokud validace běží nebo chybí ruční volba plátce
 * - server při vytvoření objednávky DIČ→DPH atomicky re-validuje (409 → volba)
 * - Idempotency-Key: síťová chyba při submitu = bezpečný retry bez duplikace
 * - po změně VAT stavu se přes event `checkout:vat-changed` překreslí souhrn košíku
 */
export default function CheckoutForm(props: CheckoutProps) {
    // Překlady se načítají z gettext katalogu (stejný .mo jako NetteTranslator);
    // formulář se vykreslí až s načteným katalogem (žádné probliknutí msgid)
    return (
        <I18nProvider url={props.translationsUrl} loading={<CheckoutSkeleton />}>
            <CheckoutFormInner {...props} />
        </I18nProvider>
    );
}

function CheckoutFormInner(props: CheckoutProps) {
    const { initialState: state, links } = props;
    const t = useI18n();
    const api = useMemo(() => createCheckoutApi(props.apiBaseUrl), [props.apiBaseUrl]);
    const idempotencyKey = useRef<string>(
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `ck-${Date.now()}-${Math.random()}`,
    );

    const customer = state.customer;

    const initialValues = useMemo(() => buildInitialValues(state, loadCheckoutDraft()), [state]);

    const form = useForm<CheckoutFormValues>({
        mode: 'onTouched',
        defaultValues: initialValues,
    });

    // Průběžné ukládání rozpracovaného formuláře (přežije refresh stejně jako VAT stav)
    useCheckoutDraftPersist(form);

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    // Pole predvyplnena z ARES/VIES/registru jsou readonly - fakturacni udaje
    // firmy musi odpovidat registru (odemkne je zmena ICO/DIC nebo neuspesny lookup)
    const [lockedFields, setLockedFields] = useState<string[]>([]);

    const publishSummary = useCallback((summary: BasketSummary) => {
        window.dispatchEvent(new CustomEvent('checkout:vat-changed', { detail: summary }));
    }, []);

    const applyCompanyData = useCallback((response: VatValidateResponse) => {
        // Autofill z VIES — jen neprázdné hodnoty, needitovat partner-locked pole
        if (customer?.isPartnerLocked) {
            return;
        }
        const c = response.company;
        if (!c) {
            return;
        }
        // IČO dohledané k IČ DPH (SK) — jen doplnit a bliknout, nezamykat (uživatelův vstup)
        if (c.ic) {
            form.setValue('company.ic', c.ic, { shouldValidate: true });
            flashInputs(['frm-company-ic']);
        }
        const filled: string[] = [];
        if (c.companyName) {
            filled.push('frm-company-name');
            form.setValue('company.name', c.companyName, { shouldValidate: true });
        }
        if (c.street) {
            filled.push('frm-billing-street');
            form.setValue('billing.street', c.street, { shouldValidate: true });
        }
        if (c.city) {
            filled.push('frm-billing-city');
            form.setValue('billing.city', c.city, { shouldValidate: true });
        }
        if (c.zip) {
            filled.push('frm-billing-zip');
            form.setValue('billing.zip', c.zip, { shouldValidate: true });
        }
        if (response.normalizedDic) {
            form.setValue('company.dic', response.normalizedDic);
        }
        flashInputs(filled);
        // zamknout pole potvrzena VIES (id inputu -> cesta ve formulari)
        const paths = filled.map((id) => id.replace('frm-', '').replace('-', '.'));
        setLockedFields((prev) => Array.from(new Set([...prev, ...paths])));
    }, [form, customer]);

    // Uložený VAT stav použít jen když sedí na DIČ ve formuláři — po obnově
    // konceptu se můžou lišit (uživatel přepsal DIČ a refreshnul před validací)
    const initialDicMatches = state.vatState.dic !== null
        && normalizeDic(initialValues.company.dic) === normalizeDic(state.vatState.dic);

    const vat = useVatMachine(
        api,
        initialDicMatches ? state.vatState.dic : null,
        initialDicMatches ? state.vatState.status : 'none',
        applyCompanyData,
        publishSummary,
    );

    // Vyplněné DIČ bez známého stavu (uložené u zákazníka nebo obnovené z konceptu):
    // ověřit přes VIES hned při načtení (jako legacy handleValidateCustomerDic),
    // ať uživatel vidí výsledek bez dotyku pole
    const initialValidationRef = useRef(false);
    useEffect(() => {
        if (initialValidationRef.current) {
            return;
        }
        initialValidationRef.current = true;

        const dic = form.getValues('company.dic').trim();
        if (dic !== '' && form.getValues('company_open') && state.market !== 'cz' && vat.state.kind === 'idle') {
            vat.validateInitial(dic);
        }
    }, [vat, form, state.market]);

    // neoverena data nesmi zustat zamcena (neplatne DIC, vypadek, smazani)
    const vatKind = vat.state.kind;
    useEffect(() => {
        if (vatKind === 'invalid' || vatKind === 'unavailable' || vatKind === 'idle') {
            setLockedFields([]);
        }
    }, [vatKind]);

    // Smartform našeptávač adres (CZ/SK) — plní inputy v DOM, hook je synchronizuje do formuláře
    useSmartform(props.smartform, form.setValue);

    /**
     * Smartform (i browser autofill) umí naplnit inputy bez eventu — react-hook-form
     * by pak validoval prázdné interní hodnoty. Před validací srovnáme DOM → formulář.
     */
    const syncDomAddressValues = () => {
        const paths = [
            'billing.street', 'billing.city', 'billing.zip',
            'delivery.street', 'delivery.city', 'delivery.zip',
        ] as const;
        paths.forEach((path) => {
            const el = document.getElementById(`frm-${path.replace('.', '-')}`) as HTMLInputElement | null;
            if (el && el.value !== form.getValues(path)) {
                form.setValue(path, el.value);
            }
        });
    };

    const submitHandler = form.handleSubmit(async (values) => {
        if (submittingRef.current) {
            return;
        }

        setSubmitError(null);

        if (vat.blocksSubmit && values.company_open && values.company.dic.trim() !== '') {
            // validace běží nebo chybí ruční volba — scroll na firemní sekci
            trackFunnel('CheckoutOrderBlocked', { reason: vat.state.kind });
            document.getElementById('checkout-company-section')?.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);
        try {
            // CZ/SK: telefon odchází s pevnou předvolbou trhu (input drží národní tvar);
            // IČ normalizované — odeslání Enterem nemusí projít blurem pole
            const payload = {
                ...values,
                person: { ...values.person, phone: joinPhonePrefix(state.market, values.person.phone) },
                company: { ...values.company, ic: normalizeIc(values.company.ic) },
            };
            const result = await api.createOrder(payload, idempotencyKey.current);
            clearCheckoutDraft();
            window.location.assign(result.redirectUrl);
        } catch (error) {
            submittingRef.current = false;
            setSubmitting(false);

            if (error instanceof ApiError) {
                if (error.code === 'BASKET_NOT_READY') {
                    window.location.assign(links.back);
                    return;
                }
                if (error.code === 'STOCK_ONLY_CHANGED') {
                    // košík se zkrátil na stav skladu - hlášku ukáže stránka košíku
                    trackFunnel('CheckoutOrderBlocked', { reason: 'stockOnlyChanged' });
                    window.location.assign(links.back);
                    return;
                }
                if (error.code === 'DELIVERY_NOT_OFFERED' || error.code === 'ZBOX_NOT_ALLOWED' || error.code === 'DELIVERY_PAYMENT_MISSING') {
                    // doprava/platba už pro košík neplatí (košík se mezitím změnil) a krok 3 výběr
                    // dopravy nemá — znovunačtení kroku 3 ukáže důvod jako flash a vrátí na krok 2
                    trackFunnel('CheckoutOrderBlocked', { reason: error.code });
                    window.location.reload();
                    return;
                }
                if (error.code === 'INVALID_RESPONSE') {
                    trackFunnel('CheckoutOrderError', { code: 'INVALID_RESPONSE' });
                    setSubmitError(t('The order could not be created. Please try again.'));
                    return;
                }
                if (error.code === 'VAT_CHOICE_REQUIRED') {
                    trackFunnel('CheckoutOrderBlocked', { reason: 'vatChoiceRequired' });
                    vat.forceManualChoice(values.company.dic);
                    document.getElementById('checkout-company-section')?.scrollIntoView({ behavior: 'smooth' });
                    return;
                }
                if (error.fields) {
                    // jen cesty polí, žádné hodnoty (PII)
                    trackFunnel('CheckoutValidationError', { step: 'personalData', fields: Object.keys(error.fields).join(',') });
                    Object.entries(error.fields).forEach(([path, message]) => {
                        form.setError(path as never, { type: 'server', message });
                    });
                    setSubmitError(t('Please check the highlighted fields.'));
                    return;
                }
                trackFunnel('CheckoutOrderError', { code: error.code });
                // 5xx nese jen technický anglický text (ApiErrorHandler), ne přeloženou hlášku
                setSubmitError((error.status < 500 && error.message) || t('The order could not be created. Please try again.'));
                return;
            }

            // Síťová chyba — Idempotency-Key umožňuje bezpečné opakování
            trackFunnel('CheckoutOrderError', { code: 'network' });
            setSubmitError(t('The order could not be created. Please try again.'));
        }
    });

    if (!state.basketReady) {
        // BasketPresenter v tomto případě přesměruje sám; fallback pro jistotu
        return <p className="msg msg--error">{t('The order could not be created. Please try again.')}</p>;
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={(event) => {
                    syncDomAddressValues();
                    void submitHandler(event);
                }}
                noValidate
                className="rc-checkout"
            >
                <PersonSection api={api} customer={customer} market={state.market} />
                <CompanySection api={api} state={state} vat={vat} lockedFields={lockedFields} setLockedFields={setLockedFields} />
                <BillingAddressSection state={state} backUrl={links.back} smartform={props.smartform !== null} lockedFields={lockedFields} />
                <DeliveryAddressSection state={state} smartform={props.smartform !== null} />
                <NoteSection />
                <AgreementsSection state={state} links={links} />

                {submitError && <p className="msg msg--error">{submitError}</p>}

                <SubmitBar
                    backUrl={links.back}
                    submitting={submitting}
                    disabled={submitting || vat.state.kind === 'validating'}
                />
            </form>
        </FormProvider>
    );
}

function normalizeDic(dic: string): string {
    return dic.replace(/[\s.\-]/g, '').toUpperCase();
}

/** Hodnota z konceptu vyhrává jen když něco nese — prázdná nepřepíše serverová data (např. po přihlášení). */
function mergeSection<T extends Record<string, unknown>>(base: T, draft?: Partial<T>): T {
    if (!draft) {
        return base;
    }
    const merged = { ...base };
    (Object.keys(base) as (keyof T)[]).forEach((key) => {
        const value = draft[key];
        if (typeof value === 'string' ? value.trim() !== '' : value !== undefined) {
            merged[key] = value as T[keyof T];
        }
    });
    return merged;
}

/**
 * Výchozí hodnoty formuláře: serverová data (zákazník, pickup point) doplněná
 * o rozpracovaný koncept ze sessionStorage. Server zůstává autoritou pro pickup
 * point a partner-locked firemní údaje; DIČ z perzistentního VAT stavu se doplní,
 * když koncept chybí (nová záložka), ať hláška o plátci nevisí nad prázdným polem.
 */
function buildInitialValues(state: CheckoutState, draft: CheckoutDraft | null): CheckoutFormValues {
    const customer = state.customer;

    const base: CheckoutFormValues = {
        person: {
            name: customer?.name ?? '',
            surname: customer?.surname ?? '',
            email: customer?.email ?? '',
            phone: stripPhonePrefix(state.market, customer?.phone ?? ''),
            wantContactEmail: false,
            contactEmail: customer?.contactEmail ?? '',
        },
        billing: {
            street: customer?.street ?? '',
            city: customer?.city ?? '',
            zip: customer?.zip ?? '',
        },
        delivery_open: state.pickupPoint !== null,
        delivery: {
            company: state.pickupPoint?.fields.company ?? '',
            name: '',
            surname: '',
            street: state.pickupPoint?.fields.street ?? '',
            city: state.pickupPoint?.fields.city ?? '',
            zip: state.pickupPoint?.fields.zip ?? '',
        },
        company_open: customer?.hasCompanyData ?? false,
        company: {
            name: customer?.company ?? '',
            ic: customer?.ic ?? '',
            dic: customer?.dic ?? '',
            referee: '',
        },
        note: '',
        agreements: { vop: false, createAccount: false, registration: false, newsletter: false, heureka: false },
        testing: false,
    };

    const merged: CheckoutFormValues = draft === null ? base : {
        person: mergeSection(base.person, draft.person),
        billing: mergeSection(base.billing, draft.billing),
        delivery_open: draft.delivery_open ?? base.delivery_open,
        delivery: mergeSection(base.delivery, draft.delivery),
        company_open: draft.company_open ?? base.company_open,
        company: mergeSection(base.company, draft.company),
        note: draft.note ?? base.note,
        agreements: mergeSection(base.agreements, draft.agreements),
        testing: base.testing,
    };

    if (state.pickupPoint !== null) {
        merged.delivery_open = true;
        merged.delivery = base.delivery;
    }
    if (customer?.isPartnerLocked) {
        merged.company_open = base.company_open;
        merged.company = base.company;
    }

    if (merged.company.dic.trim() === '' && state.vatState.dic !== null && state.vatState.status !== 'none') {
        merged.company_open = true;
        merged.company = { ...merged.company, dic: state.vatState.dic };
    }

    return merged;
}
